import { useMemo } from "react";

import { isReferencedNodeValue, isTriggerNode, type NodeModelWithId } from "@doitintl/cloudflow-commons";
import {
  CloudFlowNodeType,
  ModelType,
  type UnwrappedApiServiceModelDescriptor,
  type Variables,
} from "@doitintl/cmp-models";

import { useVariableContext } from "../../../../Common/VariableProvider";
import { type NodeWitOutputModel, useReferencedFieldContext } from "./useReferencedFieldContext";
import { generateCaption, getLabelDisplayInfo } from "./utils/fieldTokenUtils";

type ReferenceFieldMenuItemData = {
  name: string;
  caption?: string;
  hasContinuation: boolean;
  disabled?: boolean;
  selected?: boolean;
};

type NodeMenuItemData = ReferenceFieldMenuItemData & {
  type: "node";
  nodeId: string;
  disabled: boolean;
  displayName?: string;
};

type TokenMenuItemData = ReferenceFieldMenuItemData & {
  type: "token";
  displayName?: string;
};

type CustomTokenMenuItemData = ReferenceFieldMenuItemData & {
  type: "custom";
};

export const CUSTOM_TOKEN_PLACEHOLDER = "<add value>";

const isTriggerType = (type: CloudFlowNodeType) =>
  [CloudFlowNodeType.MANUAL_TRIGGER, CloudFlowNodeType.TRIGGER, CloudFlowNodeType.WEBHOOK_TRIGGER].includes(type);

export type MenuItemData = NodeMenuItemData | TokenMenuItemData | CustomTokenMenuItemData;

export function hasOtherReferencedFields(
  values: unknown,
  fieldPath: string,
  referenceableNodes: NodeWitOutputModel[],
  currentFieldPath = ""
): [boolean, string] {
  let referencedNodeId = "";
  let foundOther = false;
  switch (true) {
    case isArray(values): {
      values.some((paramValue, idx) => {
        [foundOther, referencedNodeId] = hasOtherReferencedFields(
          paramValue,
          fieldPath,
          referenceableNodes,
          `${currentFieldPath}[${idx}]`
        );
        return foundOther;
      });
      return [foundOther, referencedNodeId];
    }

    case isObject(values): {
      if (
        isReferencedNodeValue(values) &&
        referenceableNodes.find(({ id, type }) => values.referencedNodeId === id && !isTriggerType(type))
      ) {
        return [currentFieldPath !== fieldPath, values.referencedNodeId];
      }
      Object.entries(values).some(([paramName, paramValue]) => {
        [foundOther, referencedNodeId] = hasOtherReferencedFields(
          paramValue,
          fieldPath,
          referenceableNodes,
          `${currentFieldPath}.${paramName}`
        );
        return foundOther;
      });
      return [foundOther, referencedNodeId];
    }
  }

  return [foundOther, referencedNodeId];
}

export function useReferencedFieldMenuItems(
  fieldValue: unknown,
  fieldPath: string = "",
  _hideVariableReference: boolean = false // It will be used when variable reference is enabled in menu
) {
  const { referenceableNodes, values } = useReferencedFieldContext();
  const { globalVariables, localVariables } = useVariableContext();

  const variables = useMemo(
    () => ({
      globalVariables,
      localVariables,
    }),
    [globalVariables, localVariables]
  );

  let currentlyDisplayedPath: string[] = [];
  let menuItems: MenuItemData[] = [];
  if (isReferencedNodeValue(fieldValue) && referenceableNodes.find(({ id }) => fieldValue.referencedNodeId === id)) {
    const referencedNode = referenceableNodes.find(({ id }) => id === fieldValue.referencedNodeId);
    const isTrigger = isTriggerNode(referencedNode as unknown as NodeModelWithId);
    if (referencedNode) {
      try {
        menuItems = getNextTokens(referencedNode.outputModel, fieldValue.referencedField, isTrigger, variables);
        currentlyDisplayedPath = fieldValue.referencedField;
      } catch (error) {
        return { menuItems: [], currentlyDisplayedPath: [] };
      }

      if (menuItems.length === 0) {
        currentlyDisplayedPath = Array.from(fieldValue.referencedField);
        const currentlySelectedToken = currentlyDisplayedPath.pop();

        menuItems = getNextTokens(referencedNode.outputModel, currentlyDisplayedPath, isTrigger, variables);
        const currentlySelectedMenuItem = menuItems.find(({ name }) => name === currentlySelectedToken);
        if (currentlySelectedMenuItem) {
          currentlySelectedMenuItem.selected = true;
        }
      }
    }
  } else {
    const [thereAreOthers, otherReferencedNodeId] = hasOtherReferencedFields(values, fieldPath, referenceableNodes);

    menuItems = referenceableNodes.map(({ id, name, type }) => ({
      name,
      type: "node",
      nodeId: id,
      hasContinuation: true,
      disabled: thereAreOthers && id !== otherReferencedNodeId && !isTriggerType(type),
    }));

    /* Enable this when we have worker ready to handle variale references

    const triggerNode = referenceableNodes.find(({ type }) => isTriggerType(type));

    if (triggerNode && !hideVariableReference) {
      const hasVariables =
        (globalVariables && globalVariables.length > 0) || (localVariables && localVariables.length > 0);
      if (hasVariables) {
        menuItems.unshift({
          name: "Variables",
          type: "node",
          nodeId: triggerNode.id,
          hasContinuation: true,
          disabled: false,
        });
      }
    } */
  }

  return { menuItems, currentlyDisplayedPath };
}

function getNextTokens(
  model: UnwrappedApiServiceModelDescriptor,
  referencedField: string[],
  isTrigger?: boolean,
  variables?: Variables
): (TokenMenuItemData | CustomTokenMenuItemData)[] {
  switch (model.type) {
    case ModelType.LIST:
      return getNextTokens(model.member.model, referencedField, isTrigger, variables);

    case ModelType.STRUCTURE: {
      const [current, ...restOfReferencedField] = referencedField;
      if (current) {
        if (!model.members[current]) {
          return [];
        }
        return getNextTokens(model.members[current].model, restOfReferencedField, isTrigger, variables);
      }
      return Object.entries(model.members)
        .filter(([key]) => !isTrigger || key !== "variables")
        .map<TokenMenuItemData>(([label, { model }]) => {
          const { displayName, caption } = getLabelDisplayInfo(label, model, variables);
          return {
            name: label,
            displayName,
            caption,
            type: "token",
            hasContinuation: doesModelHasContinuation(model),
          };
        })
        .sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));
    }
    case ModelType.MAP: {
      const [current, ...restOfReferencedField] = referencedField;
      if (current) {
        return getNextTokens(model.valueMember.model, restOfReferencedField, isTrigger, variables);
      }
      return [
        {
          name: CUSTOM_TOKEN_PLACEHOLDER,
          caption: generateCaption(model.keyMember.model),
          type: "custom",
          hasContinuation: doesModelHasContinuation(model.valueMember.model),
        },
      ];
    }
    default:
      return [];
  }
}

function doesModelHasContinuation(model: UnwrappedApiServiceModelDescriptor) {
  return (
    [ModelType.STRUCTURE, ModelType.MAP].includes(model.type) ||
    (model.type === ModelType.LIST && [ModelType.LIST, ModelType.STRUCTURE].includes(model.member.model.type))
  );
}

function isObject(value: unknown): value is Record<string | number, object> {
  return value !== null && typeof value === "object";
}
function isArray<T>(value: T | T[]): value is T[] {
  return Array.isArray(value);
}
